/**
 * ConfigCachePage
 *
 * Admin page to configure the cache adapter for this org.
 * Reads/writes organisation/{org_id}/config/cache in Firestore via
 * the /admin/config/cache endpoints.
 *
 * Route: /settings/config/cache  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Select from "../../components/Inputs/Select";
import Input from "../../components/Inputs/Input";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";
import { CACHE_ADAPTER_OPTIONS } from "./configAdapterOptions";

const EMPTY_FORM = {
  adapter_type: "noop",
  redis_host: "",
  redis_port: "6379",
  redis_db: "0",
};

const ConfigCachePage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("cache");
      if (data.configured) {
        const loaded = {
          adapter_type: data.adapter_type ?? "noop",
          redis_host:   data.redis_host   ?? "",
          redis_port:   data.redis_port   ?? "6379",
          redis_db:     data.redis_db     ?? "0",
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load cache config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateServiceConfig("cache", form);
      setOriginalForm(form);
      showFeedback("Cache config saved. Adapter changes require a service restart.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save cache config.", "error");
    } finally {
      setSaving(false);
    }
  };

  const isRedis = form.adapter_type === "redis";

  return (
    <SettingsPageShell
      title="Cache Configuration"
      description="Configure the cache adapter used for user context and model config caching. Changes take effect on next service restart."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Cache Adapter <span className="font-normal text-ink-gray-400">(requires restart)</span>
            </label>
            <Select
              value={form.adapter_type}
              onChange={(v) => setForm((prev) => ({ ...prev, adapter_type: v }))}
              options={CACHE_ADAPTER_OPTIONS}
            />
          </div>

          {isRedis && (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  Redis Host
                </label>
                <Input
                  type="text"
                  placeholder="localhost"
                  value={form.redis_host}
                  onChange={(e) => setForm((prev) => ({ ...prev, redis_host: e.target.value }))}
                />
                <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                  Hostname or IP address of your Redis instance (e.g. <code className="font-mono">redis.example.com</code> or <code className="font-mono">127.0.0.1</code>).
                </p>
              </div>

              <div className="flex gap-4">
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                    Port
                  </label>
                  <Input
                    type="number"
                    placeholder="6379"
                    value={form.redis_port}
                    onChange={(e) => setForm((prev) => ({ ...prev, redis_port: e.target.value }))}
                  />
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                    Database Index
                  </label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={form.redis_db}
                    onChange={(e) => setForm((prev) => ({ ...prev, redis_db: e.target.value }))}
                  />
                  <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                    Redis logical database index (0–15). Isolates Photon AI data from other apps on the same instance.
                  </p>
                </div>
              </div>
            </>
          )}

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !isDirty(form, originalForm)}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigCachePage;
